/**
 * Canonical speaker-label parsing — the single source of truth for both the
 * backend (`backend/src/lib/speaker-labels.ts` re-exports these) and the
 * frontend (`frontend/src/lib/speaker-mapping.ts` consumes these). Before this
 * module existed, five copies of the raw-label regex and three different
 * "parse the trailing index" helpers drifted apart: some matched `Speaker_0`
 * but missed `Speaker_S0`, the talk-share aggregator was 0-indexed while the
 * display resolver was 1-indexed, and `Speaker_10` lexically sorted before
 * `Speaker_2`. This module fixes all of that in one place.
 *
 * ## Label shapes accepted
 *
 *   - `Speaker_1`            digit-only diarisation label (most adapters)
 *   - `Speaker_S1`           S-prefixed shape (newer providers, Speechmatics)
 *   - `SPEAKER_01`           uppercase + zero-padded variant
 *   - `Remote::Speaker_1`    far-end (system / remote-channel) label, namespaced
 *                            by the hybrid channel merge to stay distinct from
 *                            the room mic's speakers
 *
 * ## Index-base policy (READ THIS before changing any index math)
 *
 * Providers genuinely disagree on whether the first diarised speaker is index
 * `0` or index `1`:
 *
 *   - Most cloud adapters emit `Speaker_0`, `Speaker_1`, … (0-based).
 *   - Some recordings (Govannon's, historically) shipped `Speaker_S0` chips.
 *   - The display-name roster (`speaker_names`) is a positional array, and the
 *     historical convention for the *render-side* resolver was 1-indexed
 *     (`speaker_names[N - 1]`).
 *
 * The same array `["Alice", "Bob"]` means **different things** under the two
 * conventions — "Alice" is `Speaker_S1` under 1-indexed and `Speaker_S0` under
 * 0-indexed — so we cannot "try both indices". Instead we detect the base ONCE
 * per segment set with {@link detectIndexBase}: if any label carries `N=0` the
 * set is 0-based, otherwise 1-based. Callers thread the detected base through
 * the index lookups. {@link parseSpeakerLabelIndex} returns the RAW numeric
 * suffix unchanged (no base subtracted) — it is the caller's job to subtract
 * the detected base when projecting onto a positional roster.
 *
 * Defaults to base `1` when there's no signal (empty / nameless segment sets)
 * so unrelated call sites and legacy fixtures keep their behaviour.
 */

export type TIndexBase = 0 | 1;

/**
 * The canonical raw-label regex, anchored to a full label string. Covers the
 * digit-only, S-prefixed, uppercase zero-padded, and `Remote::`-namespaced
 * shapes. Capture group 1 is the numeric suffix.
 *
 * Not exported as a shared mutable RegExp instance with the global flag — a
 * shared `g`-flagged regex carries `lastIndex` state across calls and would
 * make `.test()` non-deterministic. Callers that need a fresh matcher build
 * their own from {@link RAW_SPEAKER_LABEL_SOURCE}.
 */
const RAW_LABEL_FULL = /^(?:Remote::)?(?:Speaker[_\s-]?S?|SPEAKER_)(\d+)$/i;

/**
 * The source string of the raw-label pattern (no anchors, no flags), exported
 * so callers can compose it into larger or global-flagged regexes (e.g. the
 * frontend's deep-string replacement pass) without redefining the shape.
 */
export const RAW_SPEAKER_LABEL_SOURCE = "(?:Remote::)?(?:Speaker[_\\s-]?S?|SPEAKER_)(\\d+)";

/**
 * The far-end (system / remote-channel) identity prefix. A hybrid capture
 * namespaces every loopback speaker as `Remote::<label>` so the far-end voices
 * stay distinct from the room mic's. Canonical here (shared) so both the backend
 * pipeline and the frontend agree on the one prefix string.
 */
export const REMOTE_SPEAKER_PREFIX = "Remote::";

/**
 * True when a label is still a raw diarisation placeholder (any accepted shape,
 * including the far-end `Remote::` variants). Trims surrounding whitespace so a
 * stray padded label still matches.
 */
export function isRawSpeakerLabel(label: string): boolean {
	return RAW_LABEL_FULL.test(label.trim());
}

/**
 * True when a label belongs to the far-end / remote (system-loopback) channel —
 * i.e. it carries the `Remote::` namespace prefix. Unlike {@link isRawSpeakerLabel}
 * this doesn't care whether the label is still a raw placeholder or an already
 * resolved name; it only asks "which channel is this?". The single source of truth
 * for the "is this a far-end label?" question across both front and back end.
 */
export function isRemoteSpeakerLabel(label: string): boolean {
	return label.startsWith(REMOTE_SPEAKER_PREFIX);
}

/**
 * Parse the RAW numeric suffix out of a raw diarisation label. Returns the
 * suffix verbatim (NOT base-adjusted) or `null` when the string isn't a raw
 * label. Subtract the detected {@link TIndexBase} yourself before indexing a
 * positional roster — see the index-base policy above.
 */
export function parseSpeakerLabelIndex(label: string): number | null {
	const m = label.trim().match(RAW_LABEL_FULL);
	if (!m) return null;
	const n = Number.parseInt(m[1], 10);
	return Number.isFinite(n) ? n : null;
}

/**
 * Detect whether a set of segments uses 0-based or 1-based diarisation labels.
 * Returns `0` if any segment carries a raw label with suffix `0`
 * (`Speaker_0` / `Speaker_S0` / `SPEAKER_00` / `Remote::Speaker_0`), otherwise
 * `1`. Pass the result to the index-aligned resolvers. See the index-base
 * policy above for why this can't be inferred per-label.
 */
export function detectIndexBase<TSeg extends { speaker: string }>(
	segments: TSeg[] | undefined | null,
): TIndexBase {
	if (!segments?.length) return 1;
	for (const s of segments) {
		if (parseSpeakerLabelIndex(s.speaker) === 0) return 0;
	}
	return 1;
}

/**
 * Strip the far-end `Remote::` identity prefix, returning the core label. The
 * raw prefixed form stays the colour / identity / rename key — only use the
 * stripped form for display or shape checks.
 */
export function stripRemotePrefix(label: string): string {
	return label.startsWith(REMOTE_SPEAKER_PREFIX) ? label.slice(REMOTE_SPEAKER_PREFIX.length) : label;
}
